import React, { useCallback, useRef } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './CarouselComponent.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useWidthContext } from '../../providers/Width';
import { Section, CarouselSlider, Image } from '../../importCL';

interface IChild {
    image?: string;
    imageAltText?: string;
    quote?: string;
    author?: string;
}

interface ICarouselChild {
    version: 'image' | 'quote';
    content: IChild;
}

interface ICarouselComponent extends MappedComponentProperties {
    version?: 'image' | 'quote';
    fullWidth?: boolean;
    carouselItems?: IChild[];
}

export const CarouselComponentEditConfig = {
    emptyLabel: 'Carousel',
    isEmpty: (props: ICarouselComponent) => {
        return !props || !props.carouselItems || props.carouselItems.length < 1;
    },
};

const CarouselChild = (props: ICarouselChild): JSX.Element => {
    if (props.version === 'quote') {
        return (
            <div className='quote-item'>
                <p className='text-grey-tundora'>&quot;{transformHTMLParserLinks(props.content.quote!)}&quot;</p>
                <p className='text-grey-tundora pt-6'>{transformHTMLParserLinks(props.content.author!)}</p>
            </div>
        );
    }
    
    return (
        <Image loading='lazy' src={props.content.image} alt={props.content.imageAltText} />
    );
};

const CarouselComponent = (props: ICarouselComponent): JSX.Element => {
    const { 
        version = 'image', 
        carouselItems, 
        fullWidth, 
    } = props;
    const { width } = useWidthContext();
    const prevArrowRef = useRef<HTMLSpanElement>(null);
    const nextArrowRef = useRef<HTMLSpanElement>(null);

    // from max of 328px, reduce max by -1px for every 2px to a min of 160px for screen width < 769
    const getDynamicMaxSlideWidth = useCallback(() => {
        if (width < 769) {
            return Math.max(135, 328 - (768 - width) / 2);
        }

        return 224;
    }, [width]);

    return (
        <Section id='carousel-component' className='CarouselComponent !p-0 relative'
            containerFluid={fullWidth}
            bgColor='white'>
            {carouselItems && carouselItems.length > 1 && (
                <>
                    {width > 768 && (
                        <>
                            <span className='CarouselComponent__prev left-0 pl-2'
                                ref={prevArrowRef}
                                role='button'
                                aria-label='Previous'
                                tabIndex={0} />
                            <span className='CarouselComponent__next right-0 pr-2'
                                ref={nextArrowRef}
                                role='button'
                                aria-label='Next'
                                tabIndex={0} />
                        </>
                    )}
                    <CarouselSlider
                        className='!py-6'
                        dots
                        infinite
                        autoplaySpeed={3000}
                        centerMode
                        slidesToScroll={1}
                        slidesToShow={1}
                        hideArrowsMobile
                        hideDefaultArrows
                        properties={{
                            slideGap: 12,
                            maxSlideWidth: getDynamicMaxSlideWidth(),
                        }}
                        customArrows={{
                            nextArrowRef: nextArrowRef,
                            previousArrowRef: prevArrowRef,
                        }}
                        slides={carouselItems.map((carouselItem, i) => (
                            <React.Fragment key={i}>
                                <CarouselChild version={version} content={carouselItem} />
                            </React.Fragment>
                        ))} />

                </>
            )}
        </Section>
    );
};

export default CarouselComponent;
